/**
 * Reserlo brand identity — one source for every surface.
 *
 * Consumed by the web app, email templates, PDF documents and the shared UI,
 * so a brand change lands everywhere at once. Everything here is **public**:
 * it is safe in a client bundle. Never add secrets, API keys or internal
 * endpoints to this file.
 *
 * Values that depend on infrastructure we do not own yet (domain, support
 * mailbox) read from the environment with a clearly marked local placeholder,
 * so the app runs locally without pretending the production address exists.
 * `getMissingBrandConfig()` lists what is still placeholder — the go-live
 * checklist reads it rather than keeping a second list by hand.
 *
 * This file does not change the upstream licence. See UPSTREAM below.
 */

/** Marks a default that is a local stand-in, not a real Reserlo address. */
const PLACEHOLDER_DOMAIN = 'reserlo.localhost'

const readEnv = (name: string): string | undefined => {
  const value = process.env[name]
  return value && value.trim().length > 0 ? value.trim() : undefined
}

export const BRAND = {
  /** Product name, as written in running text and in the wordmark. */
  name: 'Reserlo',

  /**
   * What the product is. Deliberately concrete: no time-saving, error-rate or
   * customer-count claims, none of which we can evidence yet.
   */
  tagline: 'Verhuursoftware voor reserveringen, voorraad en planning.',

  /** Longer description for meta tags and store listings. */
  description:
    'Reserlo is Nederlandse verhuursoftware. Beheer reserveringen, voorraad en planning, ' +
    'controleer beschikbaarheid en ontvang online aanvragen.',

  /** Primary UI colour of the wordmark and symbol. */
  color: '#035744',

  locale: 'nl',
  currency: 'EUR',
  timeZone: 'Europe/Amsterdam',
  /**
   * ISO-3166-1 alpha-2 home country. Used as the last-resort default for
   * country-shaped inputs (phone dialling codes, address fields) when no store
   * context is available; a store's own `settings.country` always wins.
   */
  country: 'NL',
} as const

/**
 * Upstream attribution. Reserlo is a modified distribution of Louez, which is
 * AGPL-3.0-only: the licence and this attribution stay, and network users must
 * be offered the corresponding source of the version actually served.
 *
 * `sourceOfferUrl` must point at the exact deployed revision before go-live.
 */
export const UPSTREAM = {
  project: 'Louez',
  author: 'Synapsr',
  repository: 'https://github.com/Synapsr/Louez',
  licence: 'AGPL-3.0-only',
  get sourceOfferUrl(): string | undefined {
    return readEnv('RESERLO_SOURCE_OFFER_URL')
  },
} as const

/** Public domain of this Reserlo instance. */
export function getBrandDomain(): string {
  return readEnv('NEXT_PUBLIC_APP_DOMAIN') ?? PLACEHOLDER_DOMAIN
}

/** Absolute public URL of this Reserlo instance. */
export function getBrandUrl(): string {
  return readEnv('NEXT_PUBLIC_APP_URL') ?? `https://${PLACEHOLDER_DOMAIN}`
}

/**
 * Mailbox for Reserlo's own account email (not a renter's customer email).
 * Returns undefined while unconfigured so callers can hide a support link
 * instead of printing an address that bounces.
 */
export function getSupportEmail(): string | undefined {
  return readEnv('RESERLO_SUPPORT_EMAIL')
}

/** Sender name on Reserlo's own account email. Never used for renter email. */
export function getAccountEmailFromName(): string {
  return BRAND.name
}

/**
 * Production values that are still placeholders. Empty array means the brand
 * surface is fully configured.
 */
export function getMissingBrandConfig(): string[] {
  const missing: string[] = []
  if (!readEnv('NEXT_PUBLIC_APP_DOMAIN')) missing.push('NEXT_PUBLIC_APP_DOMAIN')
  if (!readEnv('NEXT_PUBLIC_APP_URL')) missing.push('NEXT_PUBLIC_APP_URL')
  if (!getSupportEmail()) missing.push('RESERLO_SUPPORT_EMAIL')
  if (!UPSTREAM.sourceOfferUrl) missing.push('RESERLO_SOURCE_OFFER_URL')
  return missing
}
